import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import type { ReactNode } from 'react';
import type { AisleCategory, Store } from '../../domain/types.js';
import { addDays, isoDate, weekdayOf } from '../../domain/calendar.js';
import {
  emptyPlan, listPlans, loadLibrary, loadPlan, loadSettings, savePlan, saveLibrary, saveSettings,
  type StoredLunch, type StoredPlan, type StoredStaple,
} from './db.js';
import { DEFAULT_SETTINGS, type Settings } from './settings.js';
import { EMPTY_LIBRARY, type CustomMeal, type Library, type Rating } from './library.js';
import type { ItemMeta } from '../sync/merge.js';
import { deviceId } from '../sync/device.js';
import {
  PLAN_STORE_KEY, cookKey, dinnerKey, favKey, lunchKey, manualKey, pantryKey, recipeKey, stapleKey, tickKey,
} from '../sync/keys.js';

/**
 * Every edit stamps the item it touched.
 *
 * This is what makes per-item merge possible, and it lives in the reducer
 * rather than in the save path on purpose: the save effect sees only the
 * finished plan, so it cannot tell WHICH item changed — and stamping all of
 * them on every keystroke would make the last edit win the whole week, which
 * is precisely the whole-plan last-write-wins this phase exists to avoid.
 */
function touch(meta: Record<string, ItemMeta> | undefined, keys: string[]): Record<string, ItemMeta> {
  const at = new Date().toISOString();
  const by = deviceId();
  const next = { ...(meta ?? {}) };
  for (const key of keys) next[key] = { updatedAt: at, updatedBy: by };
  return next;
}

/**
 * A delete leaves a dated tombstone behind. Without one there is nothing to
 * compare against the other phone's older copy, so the stale edit always looks
 * newer than an absence and the deleted item comes back.
 */
function bury(meta: Record<string, ItemMeta> | undefined, keys: string[]): Record<string, ItemMeta> {
  const at = new Date().toISOString();
  const by = deviceId();
  const next = { ...(meta ?? {}) };
  for (const key of keys) next[key] = { updatedAt: at, updatedBy: by, deleted: true };
  return next;
}

type Action =
  | { type: 'load'; plan: StoredPlan }
  | { type: 'setDinner'; date: string; mealId: string; spansDays: number; portions: number | null }
  | { type: 'clearDinner'; date: string }
  | { type: 'setLunch'; date: string; personId: string; componentIds: string[] }
  | { type: 'setStaples'; staples: StoredStaple[] }
  | { type: 'setStore'; store: Store }
  | { type: 'setPantry'; ingredientId: string; have: boolean }
  | { type: 'toggleTick'; key: string }
  | { type: 'addManual'; id: string; label: string; aisle: AisleCategory }
  | { type: 'removeManual'; id: string }
  | { type: 'pruneLunches'; lunches: StoredLunch[] };

function reduce(plan: StoredPlan, action: Action): StoredPlan {
  switch (action.type) {
    case 'load':
      return action.plan;
    case 'setDinner':
      return {
        ...plan,
        dinners: [
          ...plan.dinners.filter((d) => d.date !== action.date),
          { date: action.date, mealId: action.mealId, spansDays: action.spansDays, portions: action.portions },
        ].sort((a, b) => a.date.localeCompare(b.date)),
        itemMeta: touch(plan.itemMeta, [dinnerKey(action.date)]),
      };
    case 'clearDinner':
      return {
        ...plan,
        dinners: plan.dinners.filter((d) => d.date !== action.date),
        itemMeta: bury(plan.itemMeta, [dinnerKey(action.date)]),
      };
    case 'setLunch': {
      const key = lunchKey(action.date, action.personId);
      return {
        ...plan,
        lunches: [
          ...plan.lunches.filter((l) => !(l.date === action.date && l.personId === action.personId)),
          ...(action.componentIds.length > 0
            ? [{ date: action.date, personId: action.personId, componentIds: action.componentIds }]
            : []),
        ].sort((a, b) => a.date.localeCompare(b.date) || a.personId.localeCompare(b.personId)),
        // Emptying a lunch IS removing it — the array entry goes either way, so
        // it needs a tombstone, not a stamp on something that is not there.
        itemMeta: action.componentIds.length > 0
          ? touch(plan.itemMeta, [key])
          : bury(plan.itemMeta, [key]),
      };
    }
    case 'setStaples': {
      // The staples screen hands back a whole new array, so what changed has to
      // be recovered by diffing. A staple that has gone needs a tombstone or it
      // returns on the next sync.
      const before = new Set(plan.staples.map((s) => s.ingredientId));
      const after = new Set(action.staples.map((s) => s.ingredientId));
      const removed = [...before].filter((id) => !after.has(id)).map(stapleKey);
      const present = [...after].map(stapleKey);
      return {
        ...plan,
        staples: action.staples,
        itemMeta: bury(touch(plan.itemMeta, present), removed),
      };
    }
    case 'setStore':
      return { ...plan, store: action.store, itemMeta: touch(plan.itemMeta, [PLAN_STORE_KEY]) };
    case 'setPantry':
      return {
        ...plan,
        pantry: { ...plan.pantry, [action.ingredientId]: action.have },
        itemMeta: touch(plan.itemMeta, [pantryKey(action.ingredientId)]),
      };
    case 'toggleTick':
      return {
        ...plan,
        ticked: { ...plan.ticked, [action.key]: !plan.ticked[action.key] },
        itemMeta: touch(plan.itemMeta, [tickKey(action.key)]),
      };
    case 'addManual':
      return {
        ...plan,
        manual: [...plan.manual, { id: action.id, label: action.label, aisle: action.aisle }],
        itemMeta: touch(plan.itemMeta, [manualKey(action.id)]),
      };
    case 'pruneLunches': {
      const kept = new Set(action.lunches.map((l) => lunchKey(l.date, l.personId)));
      const gone = plan.lunches
        .map((l) => lunchKey(l.date, l.personId))
        .filter((key) => !kept.has(key));
      return { ...plan, lunches: action.lunches, itemMeta: bury(plan.itemMeta, gone) };
    }
    case 'removeManual': {
      // Drop the tick with the item. Leaving it behind means a later item that
      // happens to reuse the id inherits a tick nobody made.
      const { [action.id]: _removed, ...ticked } = plan.ticked;
      return {
        ...plan,
        manual: plan.manual.filter((m) => m.id !== action.id),
        ticked,
        // Both the item AND its tick are gone, so both need burying. Burying
        // only the item would let the tick sync back and re-tick an item that
        // no longer exists.
        itemMeta: bury(plan.itemMeta, [manualKey(action.id), tickKey(action.id)]),
      };
    }
  }
}

export interface PlanContext {
  ready: boolean;
  plan: StoredPlan;
  settings: Settings;
  library: Library;
  setDinner(date: string, mealId: string, spansDays: number, portions: number | null): void;
  clearDinner(date: string): void;
  setLunch(date: string, personId: string, componentIds: string[]): void;
  setStaples(staples: StoredStaple[]): void;
  setStore(store: Store): void;
  setPantry(ingredientId: string, have: boolean): void;
  toggleTick(key: string): void;
  addManual(label: string, aisle: AisleCategory): void;
  removeManual(id: string): void;
  toggleFavourite(mealId: string): void;
  // `null` is a real answer: it means asked and dismissed. Recording it is
  // what stops the After-The-Shop prompt coming back after a skip — that
  // screen only renders on the plan view, so it unmounts on every tab
  // change, and a skip held in component state there would be undone the
  // moment someone taps away.
  rateMeal(mealId: string, cookedOn: string, rating: Rating | null): void;
  /** Create, or replace a recipe with the same id. */
  saveRecipe(meal: CustomMeal): void;
  /** Hide a recipe from the picker. It stays resolvable for any planned week. */
  archiveRecipe(id: string): void;
  updateSettings(next: Settings): void;
  /**
   * Re-read everything from IndexedDB.
   *
   * A sync merges into storage, not into React state, so without this the
   * screen would keep showing the pre-merge plan until the next remount — which
   * on the shopping list is exactly the moment it matters most.
   */
  reload(): void;
}

const Ctx = createContext<PlanContext | null>(null);

export function usePlan(): PlanContext {
  const value = useContext(Ctx);
  if (!value) throw new Error('usePlan must be used inside a <PlanProvider>');
  return value;
}

/**
 * Manual item ids must survive a reload. A module-level counter does not: it
 * resets to zero while IndexedDB keeps yesterday's items, so the next "Bin
 * bags" gets an id an existing row already holds. Ticks are keyed by that id,
 * so two unrelated items would share a tick and removing one would delete both.
 */
function newManualId(): string {
  const webCrypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function PlanProvider({
  weekStart,
  children,
}: {
  weekStart: string;
  children: ReactNode;
}): React.JSX.Element {
  const [ready, setReady] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [library, setLibrary] = useState<Library>(EMPTY_LIBRARY);
  const [plan, dispatch] = useReducer(reduce, emptyPlan(weekStart, DEFAULT_SETTINGS.defaultStore));

  // Load this week, its settings and the library.
  useEffect(() => {
    let live = true;
    setReady(false);
    void (async () => {
      const [loadedSettings, loadedLibrary] = await Promise.all([loadSettings(), loadLibrary()]);
      const existing = await loadPlan(weekStart);
      if (!live) return;
      setSettings(loadedSettings);
      setLibrary(loadedLibrary);
      // A brand-new week starts with the usual staples already in it. Seeding
      // here rather than from the Staples screen matters: the screen unmounts
      // whenever someone taps another tab, and an empty list is
      // indistinguishable from an untouched one — so a screen-level guard
      // would silently put back everything a person had deliberately unticked.
      // This is the one moment that genuinely means "a fresh week".
      let startingPlan = existing;
      if (!startingPlan) {
        // Carry last week's cupboard answers forward. If the oil was in the
        // cupboard last Sunday it probably still is, and re-answering the same
        // dozen questions every week is the reason people skip this screen.
        const lastWeek = await loadPlan(addDays(isoDate(weekStart), -7));
        startingPlan = {
          ...emptyPlan(weekStart, loadedSettings.defaultStore),
          staples: loadedLibrary.staples
            .filter((staple) => staple.usuallyOn)
            .map((staple) => ({ ingredientId: staple.ingredientId, qty: staple.qty, unit: staple.unit })),
          pantry: { ...(lastWeek?.pantry ?? {}) },
        };
      }
      if (!live) return;
      dispatch({ type: 'load', plan: startingPlan });
      setReady(true);
    })();
    return () => {
      live = false;
    };
  }, [weekStart, reloadToken]);

  // Autosave. Nobody presses save in a shop.
  useEffect(() => {
    if (!ready) return;
    void savePlan(plan);
  }, [ready, plan]);

  // The library outlives any single week, so it gets its own write-through.
  useEffect(() => {
    if (!ready) return;
    void saveLibrary(library);
  }, [ready, library]);

  const value = useMemo<PlanContext>(
    () => ({
      ready,
      plan,
      settings,
      library,
      setDinner: (date, mealId, spansDays, portions) =>
        dispatch({ type: 'setDinner', date, mealId, spansDays, portions }),
      clearDinner: (date) => dispatch({ type: 'clearDinner', date }),
      setLunch: (date, personId, componentIds) =>
        dispatch({ type: 'setLunch', date, personId, componentIds }),
      reload: () => setReloadToken((n) => n + 1),
      setStaples: (staples) => dispatch({ type: 'setStaples', staples }),
      setStore: (store) => dispatch({ type: 'setStore', store }),
      setPantry: (ingredientId, have) => dispatch({ type: 'setPantry', ingredientId, have }),
      toggleTick: (key) => dispatch({ type: 'toggleTick', key }),
      addManual: (label, aisle) => dispatch({ type: 'addManual', id: newManualId(), label, aisle }),
      removeManual: (id) => dispatch({ type: 'removeManual', id }),
      // No saveLibrary() inside these updaters. React may invoke a state
      // updater more than once, so a write placed there fires more than once
      // too. The effect above owns persistence, exactly as the plan's does.
      toggleFavourite: (mealId) => {
        setLibrary((prev) =>
          prev.favourites.includes(mealId)
            ? {
                ...prev,
                favourites: prev.favourites.filter((id) => id !== mealId),
                itemMeta: bury(prev.itemMeta, [favKey(mealId)]),
              }
            : {
                ...prev,
                favourites: [...prev.favourites, mealId],
                itemMeta: touch(prev.itemMeta, [favKey(mealId)]),
              },
        );
      },
      rateMeal: (mealId, cookedOn, rating) => {
        setLibrary((prev) => ({
          ...prev,
          cookLog: [
            ...prev.cookLog.filter((e) => !(e.mealId === mealId && e.cookedOn === cookedOn)),
            { mealId, cookedOn, rating },
          ],
          // Stamped even when `rating` is null, because null is a real answer —
          // asked and dismissed — and it has to beat an older rating from the
          // other phone rather than being mistaken for no entry at all.
          itemMeta: touch(prev.itemMeta, [cookKey(mealId, cookedOn)]),
        }));
      },
      saveRecipe: (meal) => {
        setLibrary((prev) => ({
          ...prev,
          customMeals: [...prev.customMeals.filter((m) => m.id !== meal.id), meal]
            .sort((a, b) => a.id.localeCompare(b.id)),
          itemMeta: touch(prev.itemMeta, [recipeKey(meal.id)]),
        }));
      },
      archiveRecipe: (id) => {
        setLibrary((prev) => {
          const found = prev.customMeals.find((m) => m.id === id);
          if (!found || found.archived) return prev;
          return {
            ...prev,
            customMeals: prev.customMeals.map((m) => (m.id === id ? { ...m, archived: true } : m)),
            // A stamp, NOT a tombstone: archiving is a value change. A buried
            // recipe would vanish from the other phone and break its weeks.
            itemMeta: touch(prev.itemMeta, [recipeKey(id)]),
          };
        });
      },
      updateSettings: (next) => {
        const stampedSettings: Settings = {
          ...next,
          updatedAt: new Date().toISOString(),
          updatedBy: deviceId(),
        };
        setSettings(stampedSettings);
        void saveSettings(stampedSettings);
        // Narrowing someone's nursery days orphans any lunch already planned
        // on a day they no longer have. Such a lunch renders no slot, so it
        // cannot be removed from the lunches screen — and the engine THROWS on
        // it, which would white-screen the cupboard check and the list.
        const stillValid = plan.lunches.filter((lunch) => {
          const person = next.people.find((p) => p.id === lunch.personId);
          return person?.lunchDays.includes(weekdayOf(isoDate(lunch.date))) ?? false;
        });
        if (stillValid.length !== plan.lunches.length) {
          dispatch({ type: 'pruneLunches', lunches: stillValid });
        }

        // The reducer above only ever sees the ONE week currently loaded into
        // React state. Nursery days change mid-term, and every OTHER stored
        // week can hold a lunch on a day the person no longer has — a week not
        // open right now would otherwise sit on disk with an orphaned lunch
        // the engine throws on the moment it is ever reopened (a stale lunch
        // bricks the screen that would have fixed it). Sweep every stored plan
        // directly rather than waiting for someone to revisit each week.
        void (async () => {
          for (const stored of await listPlans()) {
            if (stored.weekStart === plan.weekStart) continue;
            const kept = stored.lunches.filter((lunch) => {
              const person = next.people.find((p) => p.id === lunch.personId);
              return person?.lunchDays.includes(weekdayOf(isoDate(lunch.date))) ?? false;
            });
            if (kept.length !== stored.lunches.length) {
              await savePlan({ ...stored, lunches: kept });
            }
          }
        })();
      },
    }),
    [ready, plan, settings, library],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
